import React from "react";
import { makeColumnContentForCard } from "collections/Cards";
import {
  PolyIcon,
  ToolTip,
  ConnectionStatusIcon,
  ProductImageInContainer
} from "elements";
import { getStatusString } from "elements/ConnectionStatusIndicator/ConnectionStatusIndicator";
import { Link } from "polyrouter";
import i18n, { useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import { showLastDetected } from "./DeviceDetails";
import "./DeviceDetails.scss";
import { generatePath } from "react-router";

type TargetEntityType = {
  hardwareModel: string | null | undefined;
  connected: boolean | null | undefined;
  macAddress: string | null | undefined;
  lastDetected: string;
  softwareVersion: string | null | undefined;
  name: string | null | undefined;
  serialNumber: string | null | undefined;
};

type ConnectedEntityType = {
  connected: boolean;
  entityId: string;
  targetEntityId: string;
  targetEntity: TargetEntityType;
};

const ConnectedEntity: React.FC<ConnectedEntityType> = ({
  connected,
  targetEntityId,
  targetEntity
}) => {
  const { t } = useTranslation();
  const {
    connected: targetEntityConnected,
    hardwareModel,
    lastDetected,
    macAddress,
    name,
    serialNumber,
    softwareVersion
  } = targetEntity;

  const detailsUrl = generatePath("/manage/inventory/details/:targetEntityId", {
    targetEntityId
  });

  const statusString = getStatusString(!!targetEntityConnected);

  const fields = [
    {
      key: "model",
      label: "",
      content: (
        <>
          <ToolTip
            iconTitle={t("STATUS_INDICATORS")}
            className="secondary-device-link align-tooltip-icon"
            position="bottom"
            size="sm"
            iconReplacement={
              <PolyIcon
                title=""
                icon={connected ? "linked_yes" : "linked_no"}
                className={`linked-icon ${
                  connected ? "" : "disconnected-link"
                }`}
                data-testid={`connected-icon-${
                  serialNumber ? serialNumber : macAddress
                }`}
              />
            }
          >
            <p
              data-testid={`tooltip-text-${
                serialNumber ? serialNumber : macAddress
              }`}
            >
              {connected ? t("DEVICE_CONNECTED") : t("DEVICE_DISCONNECTED")}
            </p>
          </ToolTip>
          <div className="text-center mb-1">
            <ProductImageInContainer
              hardwareModel={hardwareModel || name || null}
              className={connected ? "" : "disconnected"}
              maxHeight={80}
              maxWidth={120}
            />
            <div>
              {targetEntityConnected !== null && (
                <ToolTip
                  iconTitle={i18n.t("STATUS_INDICATORS")}
                  className="secondary-device-status"
                  position="bottom"
                  size="xs"
                  iconReplacement={
                    <ConnectionStatusIcon
                      connected={!!targetEntityConnected}
                      className="mb-5 ml-auto"
                    />
                  }
                >
                  <p>{statusString}</p>
                </ToolTip>
              )}
              <Link
                to={detailsUrl}
                className="link ml-1"
                data-testid={`link-to-${name}`}
              >
                {hardwareModel ? hardwareModel : name}
              </Link>
            </div>
          </div>
        </>
      )
    },
    {
      key: "id",
      label: t(serialNumber ? "SERIAL_NUMBER" : "MAC_ADDRESS"),
      content: (
        <p>{handleNullFields(serialNumber ? serialNumber : macAddress)}</p>
      )
    },
    {
      key: "software-version",
      label: t("SOFTWARE_VERSION"),
      content: <p className="m-0">{handleNullFields(softwareVersion)}</p>
    }
  ];

  // only add in the lastDetected date if the device is not connected
  if (!connected && lastDetected) {
    showLastDetected(
      fields,
      lastDetected,
      `last-detected-${serialNumber ? serialNumber : macAddress}`
    );
  }

  const columnNumbers = 4;

  return (
    <div className="nested-links">
      <div
        className={"card table-card details-card connected-entity"}
        data-testid={`${name}-card`}
      >
        {makeColumnContentForCard(fields, columnNumbers)}
      </div>
    </div>
  );
};

export default ConnectedEntity;
